import * as PIXI from 'pixi.js';
import type { TextAsset } from './TextAsset';
import type { TextLineMetrics } from './TextLineMetrics';
import { TextWord } from './TextWord';
import type { WordElementSprite } from './WordElementSprite';

/**
 * TextLine is a container which holds one or more TextWords, corresponding to a single line.
 *
 */
export class TextLine extends PIXI.Container {
    classType = 'TextLine';

    #lineMetrics: TextLineMetrics;
    #textAsset: TextAsset;

    constructor(metrics: TextLineMetrics, textAsset: TextAsset) {
        super();
        this.#lineMetrics = metrics;
        this.#textAsset = textAsset;
    }

    /**
     * Get bounds relative to the parent TextAsset.
     */
    getRelativeBounds(): PIXI.Rectangle {
        const parentBounds = this.#textAsset.getBounds(true);

        const wordElementSprites = this.wordElementSprites;
        const bounds = this.wordElementSprites.reduce(
            (accumulator, currentValue) => accumulator.enlarge(currentValue.getBounds(true)),
            wordElementSprites.length > 0 ? wordElementSprites[0].getBounds(true) : new PIXI.Rectangle()
        );

        bounds.x -= parentBounds.x;
        bounds.y -= parentBounds.y;

        return bounds;
    }

    get metrics(): TextLineMetrics {
        return this.#lineMetrics;
    }

    get textWords(): TextWord[] {
        const textWords = this.children.filter((child) => {
            return child instanceof TextWord;
        });
        return textWords as TextWord[];
    }

    get hasVisibleText(): boolean {
        return this.#lineMetrics.getBounds().width > 0;
    }

    get wordElementSprites(): WordElementSprite[] {
        return this.textWords.flatMap(textWord => textWord.wordElementSprites);
    }

    _addInternalChild(child) {
        return super.addChild(child);
    }

    _addInternalChildAt(child, index) {
        return super.addChildAt(child, index);
    }

    _removeInternalChild(child) {
        return super.removeChild(child);
    }
}
